// @ts-nocheck
// Sheet pra admin gerenciar marcos do clube/grupo:
// - Lista os marcos existentes (active + closed) com indicador visual
// - Form inline pra criar novo marco (label + página alvo + deadline opcional)
// - Botão "Fechar marco" em marcos ativos
import React, { useEffect, useState } from 'react';
import { Icon } from '../icons';
import { Sheet } from '../common';

export const MilestonesSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  term: 'clube' | 'grupo';
  bookTotalPages?: number;
  milestones: any[];
  loading?: boolean;
  onCreate: (payload: { label: string; targetPage: number; deadline?: string; chapterRef?: string }) => Promise<void>;
}> = ({ open, onClose, term, bookTotalPages, milestones, loading = false, onCreate }) => {
  const [showForm, setShowForm] = useState(false);
  const [label, setLabel] = useState('');
  const [targetPage, setTargetPage] = useState('');
  const [deadline, setDeadline] = useState('');
  const [chapterRef, setChapterRef] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setShowForm(false);
      setLabel(''); setTargetPage(''); setDeadline(''); setChapterRef('');
      setError(null);
    }
  }, [open]);

  // Sugestão automática de label baseada no targetPage
  const suggestLabel = (page: string) => {
    const n = parseInt(page, 10);
    if (Number.isFinite(n) && n > 0) setLabel(`Página ${n}`);
  };

  const handleCreate = async () => {
    setError(null);
    const page = parseInt(targetPage, 10);
    const lbl = label.trim();
    if (!lbl) return setError('Nome do marco obrigatório');
    if (!Number.isFinite(page) || page < 1) return setError('Página alvo inválida');
    if (bookTotalPages && page > bookTotalPages) return setError(`Livro tem ${bookTotalPages} páginas`);

    setSaving(true);
    try {
      await onCreate({
        label: lbl,
        targetPage: page,
        deadline: deadline || undefined,
        chapterRef: chapterRef.trim() || undefined,
      });
      setShowForm(false);
      setLabel(''); setTargetPage(''); setDeadline(''); setChapterRef('');
    } catch (e: any) {
      setError(e?.message || 'Erro ao criar marco');
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle: React.CSSProperties = {
    width: '100%', padding: '12px 14px', borderRadius: 12,
    border: '1px solid var(--line)',
    background: 'var(--paper)',
    fontSize: 14, color: 'var(--ink)', outline: 'none',
    fontFamily: 'inherit',
  };
  const labelStyle: React.CSSProperties = {
    fontFamily: 'Geist Mono, monospace',
    fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
    color: 'var(--ink-3)', fontWeight: 600, marginTop: 12, marginBottom: 6,
    display: 'block',
  };

  return (
    <Sheet open={open} onClose={onClose} title={`Marcos do ${term}`}>
      <div style={{ paddingBottom: 8 }}>
        {loading && milestones.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
            Carregando marcos…
          </div>
        )}

        {!loading && milestones.length === 0 && !showForm && (
          <div style={{
            padding: 24, textAlign: 'center',
            border: '1px dashed var(--line-strong)',
            borderRadius: 16,
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✦</div>
            <div className="serif" style={{ fontSize: 18, lineHeight: 1.2 }}>
              Nenhum marco ainda
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.5 }}>
              Marcos guiam o ritmo coletivo. Defina por página ("Pág 100"), <br/>
              ou por capítulo ("Cap. 5") — admin escolhe.
            </div>
          </div>
        )}

        {!loading && milestones.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            {milestones.map((m: any) => {
              const isActive = m.state === 'active';
              return (
                <div key={m.id} style={{
                  padding: '12px 14px',
                  borderRadius: 14,
                  background: isActive ? 'linear-gradient(155deg, #1A1614 0%, #2A2320 100%)' : 'var(--paper-2)',
                  border: '1px solid ' + (isActive ? 'var(--ember)' : 'var(--line)'),
                  color: isActive ? '#F3ECDE' : 'var(--ink)',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: isActive ? 'var(--ember)' : 'var(--paper-3)',
                    color: isActive ? 'var(--paper)' : 'var(--ink-3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'Geist Mono, monospace',
                    fontSize: 13, fontWeight: 600,
                    flexShrink: 0,
                  }}>{isActive ? '✦' : m.n}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="serif" style={{
                      fontSize: 16, lineHeight: 1.2,
                      color: isActive ? '#F3ECDE' : 'var(--ink)',
                    }}>
                      {m.label}
                    </div>
                    <div className="mono" style={{
                      fontSize: 9.5, letterSpacing: '0.08em', marginTop: 2,
                      color: isActive ? 'rgba(243,236,222,0.7)' : 'var(--ink-3)',
                    }}>
                      {isActive ? 'ATIVO' : 'FECHADO'} · pág {m.targetPage}
                      {m.chapterRef ? ` · ${m.chapterRef}` : ''}
                      {m.deadline ? ` · até ${m.deadline}` : ''}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && milestones.some((m: any) => m.state === 'active') && (
          <div style={{
            marginTop: 12, padding: '10px 14px', borderRadius: 12,
            background: 'color-mix(in oklab, var(--ember) 8%, var(--paper-2))',
            border: '1px dashed color-mix(in oklab, var(--ember) 30%, transparent)',
            fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.45,
          }}>
            <strong style={{ color: 'var(--ember)' }}>✦</strong> O marco fecha quando você publicar
            um <strong>Insight Final</strong> no Estudo (FAB "+" → tipo 🎯).
            Esse é o gesto que sela uma fase da leitura.
          </div>
        )}

        {!showForm && (
          <button onClick={() => setShowForm(true)} style={{
            width: '100%', marginTop: 14,
            padding: '14px 18px', borderRadius: 14,
            background: 'var(--ember)', color: 'var(--paper)',
            fontSize: 14, fontWeight: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            border: 'none', cursor: 'pointer',
            boxShadow: '0 12px 24px -10px rgba(199,83,26,0.55)',
          }}>
            <Icon name="plus" size={15} color="var(--paper)" stroke={2.4}/>
            Criar novo marco
          </button>
        )}

        {showForm && (
          <div style={{
            marginTop: 16, padding: 16, borderRadius: 16,
            background: 'var(--paper-2)', border: '1px solid var(--line)',
          }}>
            <div className="serif" style={{ fontSize: 18, marginBottom: 4 }}>
              Novo marco
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>
              Membros que chegarem na página alvo veem o marco como ativo no estudo.
            </div>

            <label style={labelStyle}>Página alvo</label>
            <input
              type="number" inputMode="numeric" min={1}
              value={targetPage}
              onChange={e => {
                setTargetPage(e.target.value);
                if (!label) suggestLabel(e.target.value);
              }}
              placeholder={bookTotalPages ? `Ex: ${Math.ceil(bookTotalPages / 4)}` : 'Ex: 100'}
              style={fieldStyle}
            />
            {bookTotalPages && (
              <div className="mono" style={{ fontSize: 9, color: 'var(--ink-4)', marginTop: 4, letterSpacing: '0.06em' }}>
                Livro tem {bookTotalPages} páginas no total
              </div>
            )}

            <label style={labelStyle}>Nome do marco</label>
            <input
              value={label} onChange={e => setLabel(e.target.value.slice(0, 80))}
              placeholder="Ex: Página 100, Fim da parte 1, etc"
              style={fieldStyle}
            />

            <label style={labelStyle}>Capítulo (opcional)</label>
            <input
              value={chapterRef} onChange={e => setChapterRef(e.target.value.slice(0, 40))}
              placeholder="Ex: Cap. 5-7"
              style={fieldStyle}
            />

            <label style={labelStyle}>Prazo (opcional)</label>
            <input
              type="date" value={deadline} onChange={e => setDeadline(e.target.value)}
              style={fieldStyle}
            />

            {error && (
              <div style={{
                marginTop: 10, padding: '8px 10px', borderRadius: 8,
                background: 'var(--ember-soft)', color: 'var(--ember)',
                fontSize: 12, fontWeight: 500,
              }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={handleCreate} disabled={saving} style={{
                flex: 1, padding: '12px', borderRadius: 12,
                background: saving ? 'var(--paper-3)' : 'var(--ember)',
                color: saving ? 'var(--ink-4)' : 'var(--paper)',
                fontSize: 14, fontWeight: 500,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                border: 'none', cursor: saving ? 'wait' : 'pointer',
              }}>
                {saving ? 'Criando…' : 'Criar marco'}
                {!saving && <Icon name="check" size={14} color="var(--paper)" stroke={2.4}/>}
              </button>
              <button onClick={() => { setShowForm(false); setError(null); }} disabled={saving} style={{
                padding: '12px 16px', borderRadius: 12,
                background: 'transparent',
                color: 'var(--ink-3)',
                fontSize: 13,
                border: 'none', cursor: 'pointer',
              }}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </Sheet>
  );
};
